import React, { useEffect, useState, useCallback } from 'react';
import { DatasetStatistic, Dataset } from '../../../types/dataset';
import { AlgorithmVersion } from '../../../types';
import { AlgorithmStatistic } from '../../../types/result';
import { Bar } from 'react-chartjs-2';

type Props = {
  statistics: DatasetStatistic[];
  selectedDataset: null | Dataset;
  selectedDatasetStatistics: null | DatasetStatistic;
  compareVersionId: number | null;
  algorithmVersions: AlgorithmVersion[];
};

type Object = {
  missing: number;
  wrong: number;
};

export const ChartIncorrectPerField = ({
  statistics,
  selectedDataset,
  selectedDatasetStatistics,
  compareVersionId,
  algorithmVersions,
}: Props) => {
  const [chartLabels, setChartLabels] = useState<string[]>([]);
  const [latestValues, setLatestValues] = useState<Object[]>([]);
  const [previousValues, setPreviousValues] = useState<Object[]>([]);
  const [chartDatasets, setChartDatasets] = useState<any[]>([]);

  const calculateAverages = (labels: string[], array: AlgorithmStatistic[], setFunction: any) => {
    labels.forEach(label => {
      const forAvgMissing = array.reduce(
        (total: number, next: any) =>
          total + (next.summary.labels.find((l: any) => l.name === label)?.statistics.incorrect_missing || 0),
        0
      );
      const countMissing = array.filter(stat => stat.summary.labels.find((l: any) => l.name === label)).length;
      const avgMissing = forAvgMissing / countMissing;
      const forAvgWrong = array.reduce(
        (total: number, next: any) =>
          total + (next.summary.labels.find((l: any) => l.name === label)?.statistics.incorrect_wrong || 0),
        0
      );
      const countWrong = array.filter(stat => stat.summary.labels.find((l: any) => l.name === label)).length;
      const avgWrong = forAvgWrong / countWrong;
      setFunction((prev: any) => [...prev, { missing: avgMissing, wrong: avgWrong }]);
    });
  };

  const calculateValues = useCallback(
    (labels: string[], id: number, setFunction: any) => {
      labels.forEach(label => {
        const labelItem = selectedDatasetStatistics?.stats
          .find(stat => parseInt(stat.algorithmVersion.id) === +id)
          ?.summary.labels.find((l: any) => l.name === label);
        const missing = labelItem
          ? labelItem.statistics.incorrect_missing === 0
            ? 0.06
            : labelItem.statistics.incorrect_missing
          : 0;
        const wrong = labelItem
          ? labelItem.statistics.incorrect_wrong === 0
            ? 0.06
            : labelItem.statistics.incorrect_wrong
          : 0;
        setFunction((prev: any) => [...prev, { missing: missing, wrong: wrong }]);
      });
    },
    [selectedDatasetStatistics]
  );

  const determineStatsForAverageView = useCallback(
    (compareVersionId: number | null) => {
      const latest = algorithmVersions[0];
      const statsForLabels = statistics.map(stat => {
        const filteredStats = stat.stats.filter((algorithmStat: AlgorithmStatistic) =>
          compareVersionId
            ? algorithmStat.algorithmVersion.id === latest.id ||
              parseInt(algorithmStat.algorithmVersion.id) === +compareVersionId
            : algorithmStat.algorithmVersion.id === latest.id
        );
        return filteredStats;
      });
      const stats: AlgorithmStatistic[] = [];
      statsForLabels.forEach(el => stats.push(...el));
      return stats;
    },
    [algorithmVersions, statistics]
  );

  //LABELS
  useEffect(() => {
    if (!selectedDataset) {
      setChartLabels([]);
      const stats = determineStatsForAverageView(compareVersionId);
      stats.forEach(stat => {
        stat.summary.labels.forEach(label => {
          setChartLabels(prev => (!prev.includes(label.name) ? [...prev, label.name] : prev));
        });
      });
    }

    if (selectedDataset && selectedDatasetStatistics?.stats.length) {
      setChartLabels([]);
      selectedDatasetStatistics.stats[0].summary.labels.forEach(label => {
        setChartLabels(prev => [...prev, label.name]);
      });
      if (compareVersionId && selectedDatasetStatistics.stats.length > 1) {
        const compareVersionStats = selectedDatasetStatistics.stats.find(
          stat => parseInt(stat.algorithmVersion.id) === +compareVersionId
        );
        compareVersionStats?.summary.labels.forEach(label => {
          setChartLabels(prev => (!prev.includes(label.name) ? [...prev, label.name] : prev));
        });
      }
    }
  }, [
    selectedDataset,
    selectedDatasetStatistics,
    compareVersionId,
    statistics,
    algorithmVersions,
    determineStatsForAverageView,
  ]);

  //VALUES
  useEffect(() => {
    if (!selectedDataset) {
      clearGraph();
      const latest = algorithmVersions[0];
      const stats = determineStatsForAverageView(compareVersionId);
      const previousValues = compareVersionId
        ? stats.filter(stat => parseInt(stat.algorithmVersion.id) === +compareVersionId)
        : [];
      const latestValues = stats.filter(stat => stat.algorithmVersion.id === latest.id);
      calculateAverages(chartLabels, latestValues, setLatestValues);
      if (compareVersionId && previousValues.length) {
        calculateAverages(chartLabels, previousValues, setPreviousValues);
      }
    }

    if (selectedDataset && selectedDatasetStatistics?.stats.length) {
      clearGraph();
      calculateValues(chartLabels, parseInt(selectedDatasetStatistics?.stats[0].algorithmVersion.id), setLatestValues);
      if (compareVersionId && selectedDatasetStatistics?.stats.length > 1) {
        calculateValues(chartLabels, compareVersionId, setPreviousValues);
      }
    }
  }, [
    chartLabels,
    compareVersionId,
    selectedDataset,
    selectedDatasetStatistics,
    calculateValues,
    algorithmVersions,
    determineStatsForAverageView,
  ]);

  const clearGraph = () => {
    setLatestValues([]);
    setPreviousValues([]);
  };

  //CHART DATASETS
  useEffect(() => {
    if (latestValues.length) {
      setChartDatasets([]);
      if (previousValues.length) {
        Object.keys(previousValues[0]).forEach((key, i) => {
          let dataset = {
            label: `previous ${key}`,
            data: previousValues.map((v: any) => v[key]),
            backgroundColor: i === 0 ? 'rgba(252, 158, 86, 0.4)' : 'rgba(255, 64, 64, 0.4)',
            stack: `stack 1`,
          };
          setChartDatasets(prev => [...prev, dataset]);
        });
      }
      Object.keys(latestValues[0]).forEach((key, i) => {
        let dataset = {
          label: `latest ${key}`,
          data: latestValues.map((v: any) => v[key]),
          backgroundColor: i === 0 ? 'rgba(252, 158, 86, 0.6)' : 'rgba(255, 64, 64, 0.6)',
          stack: `stack 2`,
        };
        setChartDatasets(prev => [...prev, dataset]);
      });
    }
  }, [latestValues, previousValues]);

  const data = {
    labels: chartLabels,
    datasets: chartDatasets,
  };

  const options = {
    indexAxis: 'x',
    maintainAspectRatio: false,
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        suggestedMin: 0,
        suggestedMax: 10,
      },
    },
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: 'Number of wrong fields',
        font: { size: '16px' },
      },
      tooltip: {
        enabled: true,
        intersect: true,
        callbacks: {
          label: function(tooltipItem: any, data: any) {
            const label = tooltipItem.dataset.label;
            const value = tooltipItem.formattedValue === '0,06' ? '0' : tooltipItem.formattedValue;
            return label + ': ' + value;
          },
        },
      },
    },
  };

  return (
    <>
      <Bar data={data} options={options} redraw={false} style={{ height: 350 }} />
    </>
  );
};
